import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card, CardHeader, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { BackgroundSlider } from '../components/BackgroundSlider';
import { ArrowRight, Check, Brain, Clock, TrendingDown, AlertCircle, Zap, Database, Search, FileText, Cpu, Layers, Users, Briefcase, GraduationCap, BookOpen } from 'lucide-react';
interface AIResearchMasteryPageProps {
  onNavigate?: (path: string) => void;
}
// Declare Paystack on window object
declare global {
  interface Window {
    PaystackPop: any;
  }
}
export function AIResearchMasteryPage({
  onNavigate
}: AIResearchMasteryPageProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [showForm, setShowForm] = useState(false);
  // Handle scroll to payment if hash is present
  useEffect(() => {
    if (window.location.hash === '#payment') {
      setTimeout(() => {
        const element = document.getElementById('payment');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  const handlePayment = () => {
    if (!email || !name) {
      alert('Please enter your name and email to proceed with payment');
      setShowForm(true);
      return;
    }
    // Paystack integration
    const handler = window.PaystackPop.setup({
      key: 'pk_live_c2f71547b29d95243a012d9ef2f8e03485d942e7',
      email: email,
      amount: 550000,
      currency: 'NGN',
      ref: 'AI_RES_' + Math.floor(Math.random() * 1000000000 + 1),
      metadata: {
        custom_fields: [{
          display_name: 'Full Name',
          variable_name: 'full_name',
          value: name
        }, {
          display_name: 'Program',
          variable_name: 'program',
          value: 'AI Research Mastery 2026'
        }]
      },
      callback: function (response: any) {
        // Payment successful - redirect to WhatsApp community
        alert('Payment successful! You will now be redirected to join our WhatsApp community.');
        window.location.href = 'https://chat.whatsapp.com/D0iOO4tZcToAYXuPLUENmR';
      },
      onClose: function () {
        // Payment cancelled
        console.log('Payment cancelled');
      }
    });
    handler.openIframe();
  };
  const scrollToPayment = () => {
    const element = document.getElementById('payment');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      setShowForm(true);
    }
  };
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans selection:bg-[#00d9ff]/20 selection:text-[#1f2933]" data-id="element-433">
      {/* Load Paystack Script */}
      <script src="https://js.paystack.co/v1/inline.js" data-id="element-434"></script>

      <Navbar onNavigate={onNavigate} data-id="element-435" />

      {/* Background Slider */}
      <BackgroundSlider data-id="element-436" />

      {/* HERO SECTION */}
      <section className="relative border-b border-dashed border-[#9ca3af] py-24 sm:py-32 overflow-hidden" data-id="element-437">
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 z-10 bg-[linear-gradient(to_right,#9ca3af20_1px,transparent_1px),linear-gradient(to_bottom,#9ca3af20_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" data-id="element-438" />

        <div className="relative z-20 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-439">
          <Badge variant="secondary" className="mb-8 mx-auto font-mono text-xs tracking-wider border-[#00d9ff] text-[#00d9ff] bg-[#00d9ff]/10" data-id="element-440">
            SYSTEM_2026 // ONLINE
          </Badge>

          <h1 className="mx-auto max-w-5xl text-5xl font-bold tracking-tight sm:text-7xl mb-4 text-[#1f2933]" data-id="element-441">
            AI RESEARCH MASTERY <span className="text-[#00d9ff]" data-id="element-442">2026</span>
          </h1>

          <p className="font-mono text-sm text-[#9ca3af] mb-6 tracking-widest uppercase" data-id="element-443">
            The Only Study Method That Gets You Ahead
          </p>

          <p className="mx-auto max-w-2xl text-lg font-mono text-[#1f2933]/80 mb-10 leading-relaxed" data-id="element-444">
            Master AI tools for deep research, accelerated learning, and
            future-proof skills.
            <br data-id="element-445" />
            <span className="text-[#7c3aed] font-bold" data-id="element-446">
              For Students, Lecturers, Professionals & Business Owners.
            </span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-447">
            <Button size="large" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-449" />} onClick={scrollToPayment} className="bg-[#00d9ff] hover:bg-[#00d9ff]/90 border-[#00d9ff] text-white" data-id="element-448">
              START_LEARNING
            </Button>
            <Button variant="secondary" size="large" onClick={() => onNavigate && onNavigate('/ai-research-blueprint')} className="border-[#1f2933] text-[#1f2933] hover:bg-[#1f2933] hover:text-white" data-id="element-450">
              VIEW_CURRICULUM
            </Button>
          </div>
        </div>
      </section>

      {/* PAIN POINTS - DIAGNOSTICS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-451">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-452">
          <div className="mb-16" data-id="element-453">
            <h2 className="font-mono text-xs font-bold text-[#7c3aed] mb-2" data-id="element-454">
              01_DIAGNOSTICS
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-455">
              The Learning Crisis of 2026
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-456">
            {[{
            icon: Brain,
            title: 'Information Overload',
            desc: "Drowning in content, can't filter signal from noise."
          }, {
            icon: Clock,
            title: 'Outdated Methods',
            desc: 'Traditional study techniques are failing in the AI era.'
          }, {
            icon: TrendingDown,
            title: 'Falling Behind',
            desc: 'Skills become obsolete before you even finish learning them.'
          }, {
            icon: AlertCircle,
            title: 'No System',
            desc: 'Random learning with no structure, retention, or application.'
          }].map((item, i) => <Card key={i} variant="outlined" className="bg-transparent border-dashed border-[#9ca3af] hover:border-[#00d9ff] transition-colors group" data-id="element-457">
                <CardHeader title={item.title} className="font-bold group-hover:text-[#00d9ff] transition-colors" data-id="element-458" />
                <CardContent data-id="element-459">
                  <item.icon className="w-8 h-8 text-[#9ca3af] mb-4 group-hover:text-[#00d9ff] transition-colors" data-id="element-460" />
                  <p className="font-mono text-sm text-[#1f2933]/80" data-id="element-461">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* URGENCY PROTOCOL */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#1f2933] text-[#f5f5f7]" data-id="element-462">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-463">
          <h2 className="font-mono text-xs font-bold text-[#00d9ff] mb-8" data-id="element-464">
            02_URGENCY_PROTOCOL
          </h2>

          <blockquote className="text-3xl md:text-5xl font-bold mb-12 leading-tight" data-id="element-465">
            "By 2026, AI literacy won't be optional. It will be survival."
          </blockquote>

          <p className="text-lg text-[#9ca3af] mb-12 max-w-2xl mx-auto leading-relaxed font-mono" data-id="element-466">
            The gap between AI-powered learners and traditional learners is
            widening exponentially. Those who master AI research tools now will
            dominate their fields. Those who don't will be left behind.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-dashed border-[#9ca3af]/30 pt-12" data-id="element-467">
            <div data-id="element-468">
              <div className="text-4xl font-bold text-[#00d9ff] mb-2" data-id="element-469">10x</div>
              <div className="font-mono text-xs text-[#9ca3af]" data-id="element-470">
                FASTER RESEARCH
              </div>
            </div>
            <div data-id="element-471">
              <div className="text-4xl font-bold text-[#7c3aed] mb-2" data-id="element-472">100x</div>
              <div className="font-mono text-xs text-[#9ca3af]" data-id="element-473">
                MORE INSIGHTS
              </div>
            </div>
            <div data-id="element-474">
              <div className="text-4xl font-bold text-[#00d9ff] mb-2" data-id="element-475">∞</div>
              <div className="font-mono text-xs text-[#9ca3af]" data-id="element-476">
                LEARNING CAPACITY
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE SYSTEMS - TOOLS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-477">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-478">
          <div className="mb-16" data-id="element-479">
            <h2 className="font-mono text-xs font-bold text-[#00d9ff] mb-2" data-id="element-480">
              03_CORE_SYSTEMS
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-481">
              Your AI Research Arsenal
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-482">
            {[{
            title: 'ChatGPT Mastery',
            desc: 'Deep research, critical thinking, knowledge extraction.',
            icon: Zap
          }, {
            title: 'Gemini Advanced',
            desc: 'Multi-modal research, data analysis, complex reasoning.',
            icon: Cpu
          }, {
            title: 'Google NotebookLM',
            desc: 'Knowledge synthesis, automatic summaries, insight generation.',
            icon: BookOpen
          }, {
            title: 'Perplexity AI',
            desc: 'Academic research, citation tracking, source verification.',
            icon: Search
          }, {
            title: 'Claude',
            desc: 'Long-form analysis, document processing, ethical reasoning.',
            icon: FileText
          }, {
            title: 'Bonus Tools',
            desc: 'Consensus, Elicit, Semantic Scholar, and more.',
            icon: Layers
          }].map((tool, i) => <Card key={i} variant="elevated" className="border-l-4 border-l-[#00d9ff]" data-id="element-483">
                <CardHeader title={tool.title} className="font-bold" data-id="element-484" />
                <CardContent data-id="element-485">
                  <p className="font-mono text-sm text-[#1f2933]/70 mb-4" data-id="element-486">
                    {tool.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* TRANSFORMATION OUTCOMES */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#e5e5e7]" data-id="element-487">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-488">
          <div className="flex flex-col md:flex-row gap-16 items-center" data-id="element-489">
            <div className="w-full md:w-1/2" data-id="element-490">
              <h2 className="font-mono text-xs font-bold text-[#7c3aed] mb-2" data-id="element-491">
                04_EXPECTED_OUTPUT
              </h2>
              <h3 className="text-3xl font-bold text-[#1f2933] mb-6" data-id="element-492">
                Your Learning Transformation
              </h3>
              <p className="font-mono text-sm text-[#1f2933]/70 mb-8" data-id="element-493">
                From overwhelmed to optimized.
              </p>

              <ul className="space-y-4" data-id="element-494">
                {['Research 10x faster with AI-powered search and synthesis', 'Retain 5x more information with AI note-taking systems', 'Generate insights traditional methods would never reveal', 'Build a personal knowledge base that compounds over time', 'Stay ahead of your field with continuous AI-assisted learning', 'Master any topic in weeks, not years'].map((outcome, i) => <li key={i} className="flex items-start gap-3 border-b border-dashed border-[#9ca3af]/30 pb-2" data-id="element-495">
                    <Check className="w-5 h-5 text-[#00d9ff] flex-shrink-0 mt-0.5" data-id="element-496" />
                    <span className="text-[#1f2933] font-medium" data-id="element-497">
                      {outcome}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="w-full md:w-1/2" data-id="element-498">
              <div className="aspect-square border-2 border-dashed border-[#1f2933] p-8 relative bg-[#f5f5f7]" data-id="element-499">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 border border-[#00d9ff] rounded-full animate-pulse opacity-20" data-id="element-500"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 border border-[#7c3aed] rounded-full animate-pulse opacity-30 delay-75" data-id="element-501"></div>

                <div className="h-full flex flex-col justify-between relative z-10" data-id="element-502">
                  <div className="flex justify-between" data-id="element-503">
                    <div className="font-mono text-xs border border-[#1f2933] p-2 bg-white" data-id="element-504">
                      INPUT: CHAOS
                    </div>
                    <div className="font-mono text-xs border border-[#00d9ff] p-2 bg-[#00d9ff]/10 text-[#00d9ff]" data-id="element-505">
                      AI PROCESSING
                    </div>
                  </div>

                  <div className="text-center font-bold text-2xl tracking-widest text-[#1f2933]" data-id="element-506">
                    MASTERY
                  </div>

                  <div className="flex justify-center" data-id="element-507">
                    <div className="font-mono text-xs border-2 border-[#1f2933] p-2 bg-[#1f2933] text-white" data-id="element-508">
                      OUTPUT: CLARITY
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM PREVIEW */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-509">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-510">
          <div className="text-center mb-16" data-id="element-511">
            <h2 className="font-mono text-xs font-bold text-[#00d9ff] mb-2" data-id="element-512">
              05_TRAINING_MODULES
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933] mb-8" data-id="element-513">
              10-Week Intensive Program
            </h3>
            <Button variant="secondary" onClick={() => onNavigate && onNavigate('/ai-research-blueprint')} rightIcon={<ArrowRight className="w-4 h-4" data-id="element-515" />} data-id="element-514">
              VIEW_FULL_CURRICULUM
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-516">
            {['AI Fundamentals', 'ChatGPT Deep Dive', 'Gemini Mastery', 'NotebookLM Synthesis', 'Perplexity Research', 'Claude Analysis', 'Workflow Automation', 'AI Note-Taking', 'Knowledge Mgmt', 'Future Strategies'].map((module, i) => <div key={i} className="border border-dashed border-[#9ca3af] p-4 hover:bg-white transition-colors" data-id="element-517">
                <div className="font-mono text-xs text-[#9ca3af] mb-2" data-id="element-518">
                  MODULE_{String(i + 1).padStart(2, '0')}
                </div>
                <div className="font-bold text-sm text-[#1f2933]" data-id="element-519">{module}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* TARGET USERS */}
      <section className="py-24 border-b border-dashed border-[#9ca3af]" data-id="element-520">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-521">
          <div className="mb-16" data-id="element-522">
            <h2 className="font-mono text-xs font-bold text-[#7c3aed] mb-2" data-id="element-523">
              06_TARGET_USERS
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933]" data-id="element-524">
              Built For The Future-Ready
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-525">
            {[{
            icon: GraduationCap,
            title: 'University Students',
            desc: 'Get ahead, ace exams, research faster, stand out from peers.'
          }, {
            icon: Users,
            title: 'Lecturers',
            desc: 'Stay current, prepare better materials, research efficiently, engage students.'
          }, {
            icon: Briefcase,
            title: 'Professionals',
            desc: 'Upskill rapidly, stay relevant in your industry, advance faster.'
          }, {
            icon: TrendingDown,
            title: 'Business Owners',
            desc: 'Make informed decisions, spot trends early, stay competitive.'
          }].map((item, i) => <Card key={i} className="border-t-4 border-t-[#1f2933]" data-id="element-526">
                <CardHeader title={item.title} className="font-bold" data-id="element-527" />
                <CardContent data-id="element-528">
                  <item.icon className="w-8 h-8 text-[#1f2933] mb-4" data-id="element-529" />
                  <p className="font-mono text-sm text-[#1f2933]/80" data-id="element-530">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* OBJECTION HANDLING */}
      <section className="py-24 border-b border-dashed border-[#9ca3af] bg-[#f5f5f7]" data-id="element-531">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8" data-id="element-532">
          <h2 className="font-mono text-xs font-bold text-[#00d9ff] mb-8 text-center" data-id="element-533">
            07_FAQ_PROTOCOL
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-534">
            {[{
            q: "I don't have time.",
            a: 'This saves you time. AI research is 10x faster than traditional methods.'
          }, {
            q: "I'm not technical.",
            a: 'No coding required. Just practical, conversational AI tool usage.'
          }, {
            q: 'Will this work for my field?',
            a: 'Yes. AI research principles apply universally to any domain or major.'
          }, {
            q: 'Is this just theory?',
            a: 'No. Hands-on, practical exercises you can apply immediately.'
          }].map((faq, i) => <div key={i} className="bg-white border border-[#9ca3af] p-6" data-id="element-535">
                <h4 className="font-bold text-[#1f2933] mb-2 flex items-center gap-2" data-id="element-536">
                  <span className="text-[#00d9ff] font-mono" data-id="element-537">Q:</span> {faq.q}
                </h4>
                <p className="font-mono text-sm text-[#1f2933]/70 pl-6 border-l-2 border-[#9ca3af]" data-id="element-538">
                  {faq.a}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* INVESTMENT SECTION */}
      <section id="payment" className="py-24 border-b border-dashed border-[#9ca3af] scroll-mt-24" data-id="element-539">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8" data-id="element-540">
          <div className="bg-white border-2 border-dashed border-[#1f2933] p-8 md:p-12 text-center relative overflow-hidden" data-id="element-541">
            <div className="absolute top-0 right-0 bg-[#00d9ff] text-white font-bold text-xs px-4 py-1 font-mono" data-id="element-542">
              LIMITED TIME OFFER
            </div>

            <h2 className="font-mono text-xs font-bold text-[#7c3aed] mb-2" data-id="element-543">
              08_ACCESS_PROTOCOL
            </h2>
            <h3 className="text-3xl font-bold text-[#1f2933] mb-8" data-id="element-544">
              Your Investment in the Future
            </h3>

            {/* Pricing */}
            <div className="mb-10 p-6 bg-[#f5f5f7] inline-block rounded-lg border border-[#9ca3af]/30" data-id="element-545">
              <div className="flex flex-col items-center gap-2" data-id="element-546">
                <span className="text-xl text-[#9ca3af] line-through font-mono" data-id="element-547">
                  ₦55,000
                </span>
                <div className="flex items-baseline gap-2" data-id="element-548">
                  <span className="text-5xl font-bold text-[#1f2933]" data-id="element-549">
                    ₦5,500
                  </span>
                  <span className="text-sm text-[#9ca3af] font-mono" data-id="element-550">NGN</span>
                </div>
                <Badge variant="success" className="mt-2 bg-[#00d9ff] text-white border-none" data-id="element-551">
                  90% OFF DISCOUNT
                </Badge>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 text-left max-w-2xl mx-auto mb-10" data-id="element-552">
              <ul className="space-y-3" data-id="element-553">
                {['10 comprehensive training modules', 'Lifetime access to all materials', 'AI tool mastery guides', 'Research workflow templates'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-554">
                    <Check className="w-4 h-4 text-[#00d9ff] flex-shrink-0" data-id="element-555" />
                    <span className="text-sm font-mono text-[#1f2933]" data-id="element-556">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <ul className="space-y-3" data-id="element-557">
                {['Private community access', 'Weekly live Q&A sessions', 'Certificate of completion', 'Future updates included'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-558">
                    <Check className="w-4 h-4 text-[#00d9ff] flex-shrink-0" data-id="element-559" />
                    <span className="text-sm font-mono text-[#1f2933]" data-id="element-560">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Payment Form */}
            <div className="max-w-md mx-auto space-y-4 mb-8" data-id="element-561">
              <div className="text-left" data-id="element-562">
                <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-563">
                  FULL_NAME
                </label>
                <input type="text" placeholder="Enter your full name" value={name} onChange={e => setName(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-[#00d9ff] focus:outline-none bg-white transition-colors" required data-id="element-564" />
              </div>
              <div className="text-left" data-id="element-565">
                <label className="block text-xs font-mono text-[#9ca3af] mb-1" data-id="element-566">
                  EMAIL_ADDRESS
                </label>
                <input type="email" placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} className="w-full border-2 border-[#9ca3af] px-4 py-3 font-mono text-sm focus:border-[#00d9ff] focus:outline-none bg-white transition-colors" required data-id="element-567" />
              </div>
            </div>

            <Button size="large" onClick={handlePayment} className="w-full max-w-md bg-[#1f2933] hover:bg-[#00d9ff] hover:text-[#1f2933] hover:border-[#00d9ff] border-[#1f2933] text-white transition-all duration-300" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-569" />} data-id="element-568">
              SECURE_YOUR_SPOT
            </Button>

            <p className="font-mono text-xs text-[#9ca3af] mt-6 flex items-center justify-center gap-2" data-id="element-570">
              <span className="w-2 h-2 bg-[#00d9ff] rounded-full animate-pulse" data-id="element-571"></span>
              Secure payment via Paystack
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 text-center bg-[#1f2933] text-white" data-id="element-572">
        <div className="mx-auto max-w-4xl px-4" data-id="element-573">
          <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight" data-id="element-574">
            The Future Belongs to
            <br data-id="element-575" />
            <span className="text-[#00d9ff]" data-id="element-576">AI-Powered Learners</span>
          </h2>

          <p className="text-xl text-[#9ca3af] mb-12 font-mono" data-id="element-577">
            Join them. Or watch them pass you by.
          </p>

          <Button size="large" className="min-w-[250px] bg-[#00d9ff] hover:bg-white hover:text-[#1f2933] border-[#00d9ff] text-[#1f2933]" onClick={scrollToPayment} data-id="element-578">
            START_YOUR_TRANSFORMATION
          </Button>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-579" />
    </div>;
}